import type { TipoColisao } from './timelineCinematica'
import type { TrajetoriaFrontal } from './metricasCinematica'

export type CategoriaPista = 'veicular' | 'habitaculo' | 'ocupante'
export type IconeTipoPista = 'vidro' | 'volante' | 'painel' | 'cinto' | 'pedais' | 'coluna'

export interface PistaCena {
  id: string
  titulo: string
  subtitulo: string
  categoria: CategoriaPista
  iconeTipo: IconeTipoPista
  /** Posição relativa em coordenadas locais do veículo [x, y, z] */
  posicao3D: readonly [number, number, number]
  descricao: string
  mecanismo: string
  suspeitasPhtls: readonly string[]
  condutaCbmmg: string
}

export const TODAS_AS_PISTAS: readonly PistaCena[] = [
  {
    id: 'parabrisa_teia',
    titulo: 'Para-brisa com Efeito Teia de Aranha',
    subtitulo: 'Impacto craniano no vidro laminado',
    categoria: 'habitaculo',
    iconeTipo: 'vidro',
    posicao3D: [-0.35, 1.28, -0.45],
    descricao: 'Fratura circular concêntrica e radial clássica ("teia de aranha") na lâmina interna do para-brisa dianteiro.',
    mecanismo: 'Projeção frontal desimpedida da cabeça do ocupante sem cinto na trajetória "Up-and-Over", colidindo diretamente contra a lâmina de vidro.',
    suspeitasPhtls: [
      'Traumatismo Cranioencefálico (TCE) moderado a grave com concussão ou contusão cortical',
      'Fraturas faciais complexas (padrões Le Fort I, II e III) e trauma ocular direto',
      'Lesão na coluna cervical por mecanismo de compressão axial e hiperextensão abrupta',
      'Obstrução de vias aéreas superiores por sangramento maxilofacial e fragmentos dentários',
    ],
    condutaCbmmg: 'Estabilização manual da coluna cervical em posição neutra; aspiração imediata de vias aéreas e preparo para oxigenoterapia sob máscara;',
  },
  {
    id: 'volante_deformado',
    titulo: 'Volante Deformado / Curvado',
    subtitulo: 'Aro entortado pela compressão do esterno',
    categoria: 'habitaculo',
    iconeTipo: 'volante',
    posicao3D: [-0.42, 0.96, -0.48],
    descricao: 'Deformação plástica do aro metálico do volante, com dobra acentuada na seção superior/central em direção ao painel.',
    mecanismo: 'Desaceleração do tórax do motorista de encontro à coluna de direção, descarregando energia concentrada sobre a parede anterior da caixa torácica.',
    suspeitasPhtls: [
      'Fratura de esterno com risco imediato de Contusão Miocárdica e arritmias cardíacas',
      'Tórax instável anterior com respiração paradoxal',
      'Rotura traumática de aorta torácica por compressão da artéria contra a coluna dorsal',
      'Contusão pulmonar bilateral com redução da complacência e hemotórax/pneumotórax',
    ],
    condutaCbmmg: 'Monitorização cardíaca contínua (ECG); avaliação de simetria de pulsos periféricos; suporte ventilatório agressivo;',
  },
  {
    id: 'painel_joelho',
    titulo: 'Afundamento do Painel Inferior',
    subtitulo: 'Marcas de impacto patelar bilateral',
    categoria: 'habitaculo',
    iconeTipo: 'painel',
    posicao3D: [-0.42, 0.65, -0.68],
    descricao: 'Fratura ou afundamento côncavo no acabamento plástico inferior do painel na altura dos joelhos do condutor.',
    mecanismo: 'Mecanismo "Down-and-Under": ocupante escorrega para a frente sob o painel, impactando violentamente as patelas contra a estrutura rígida da travessa.',
    suspeitasPhtls: [
      'Fratura cominutiva ou luxação de patela',
      'Fratura diafisária de fêmur com sangramento compartimental (risco de 1000 a 1500 ml de perda volêmica)',
      'Luxação posterior da articulação coxofemoral com fratura do rebordo acetabular e lesão do nervo ciático',
    ],
    condutaCbmmg: 'Palpação cautelosa de fêmur e teste de estabilidade pélvica; alinhamento e tração suave do membro antes da extração;',
  },
  {
    id: 'cinto_tensionado',
    titulo: 'Cinto de Segurança Travado / Tensionado',
    subtitulo: 'Acionamento do pré-tensionador pirotécnico',
    categoria: 'ocupante',
    iconeTipo: 'cinto',
    posicao3D: [-0.58, 1.08, 0.08],
    descricao: 'Fita do cinto de segurança totalmente esticada e retesada, com travamento mecânico na coluna B e marcas de fricção plástica.',
    mecanismo: 'O sistema de retenção acoplou o ocupante à desaceleração do veículo, atenuando a ejeção através da queima pirotécnica do prétensionador.',
    suspeitasPhtls: [
      'Confirmação objetiva de uso eficaz de cinto de segurança (elimina ejeção)',
      'Sinal do Cinto (Seatbelt Sign): equimose e contusão tecidual ao longo da clavícula e tórax',
      'Necessidade de busca ativa de contusão pulmonar ou lesão intra-abdominal oculta',
    ],
    condutaCbmmg: 'Cortar a fita do cinto de segurança com corta-cinto de resgate somente após o posicionamento inicial dos socorristas internos;',
  },
  {
    id: 'pedaleira_intrusao',
    titulo: 'Intrusão da Pedaleira e Corta-Fogo',
    subtitulo: 'Deformação do espaço de sobrevivência dos pés',
    categoria: 'veicular',
    iconeTipo: 'pedais',
    posicao3D: [-0.42, 0.40, -1.02],
    descricao: 'Pedais de freio/embreagem deslocados para trás e para cima, com intrusão de metal da chapa corta-fogo para dentro da cabine.',
    mecanismo: 'Impacto frontal violento ou com pequena sobreposição (Small Overlap), empurrando a caixa de roda e a suspensão dianteira contra os membros inferiores.',
    suspeitasPhtls: [
      'Encarceramento físico tipo II de membros inferiores (pés e tornozelos presos mecanicamente)',
      'Fratura-luxação do tornozelo, fratura de calcâneo e lesão do mediopé (Lisfranc)',
      'Síndrome de esmagamento com isquemia prolongada de extremidade',
    ],
    condutaCbmmg: 'Técnica de rolamento ou alívio de painel com cilindro expansor e alargador hidráulico para rebater a coluna de direção e pedaleira;',
  },
  {
    id: 'coluna_b_intrusao',
    titulo: 'Intrusão Lateral na Coluna B e Soleira',
    subtitulo: 'Colapso do espaço de sobrevivência lateral',
    categoria: 'veicular',
    iconeTipo: 'coluna',
    posicao3D: [-0.88, 0.98, 0.02],
    descricao: 'A porta dianteira e a coluna B dobraram profundamente para dentro da cabine, atingindo a lateral do assento da vítima.',
    mecanismo: 'Impacto lateral perpendicular ou oblíquo contra obstáculo ou outro veículo no lado próximo (Near-Side).',
    suspeitasPhtls: [
      'Fraturas múltiplas de arcos costais homolaterais com tórax instável lateral',
      'Contusão pulmonar maciça e pneumotórax',
      'Laceração esplênica com hemoperitônio (se impacto esquerdo) ou laceração hepática (se impacto direito)',
      'Fratura instável da bacia por compressão lateral com lesão do plexo venoso pré-sacral',
    ],
    condutaCbmmg: 'Manobra de rebatimento total de lateral ou remoção da porta e coluna B com ferramenta combinada/tesoura hidráulica; aplicação de cinta pélvica;',
  },
]

/**
 * Retorna as pistas ativas para a configuração de cena atual.
 */
export function obterPistasAtivas(
  tipoColisao: TipoColisao,
  velocidadeKmh: number,
  comCinto: boolean,
  trajetoriaFrontal: TrajetoriaFrontal = 'upAndOver',
): readonly PistaCena[] {
  return TODAS_AS_PISTAS.filter((pista) => {
    switch (pista.id) {
      case 'parabrisa_teia':
        return (
          (tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira') &&
          !comCinto &&
          trajetoriaFrontal === 'upAndOver' &&
          velocidadeKmh >= 25
        )
      case 'volante_deformado':
        return (
          (tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira') &&
          !comCinto &&
          velocidadeKmh >= 35
        )
      case 'painel_joelho':
        return (
          (tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira') &&
          !comCinto &&
          trajetoriaFrontal === 'downAndUnder' &&
          velocidadeKmh >= 25
        )
      case 'cinto_tensionado':
        return comCinto && velocidadeKmh >= 30
      case 'pedaleira_intrusao':
        return (
          (tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira') &&
          velocidadeKmh >= 40
        )
      case 'coluna_b_intrusao':
        return (
          (tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento') &&
          velocidadeKmh >= 30
        )
      default:
        return false
    }
  })
}

export function pistaPorId(id: string): PistaCena | undefined {
  return TODAS_AS_PISTAS.find((p) => p.id === id)
}
